import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Dropdown,
  Modal,
  message,
  type MenuProps,
} from 'antd';
import {
  ReloadOutlined,
  ReadOutlined,
  PlusOutlined,
  RightOutlined,
  FilePdfOutlined,
  FileMarkdownOutlined,
  LinkOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { Knowledge } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { CreateKnowledgeModal } from './CreateKnowledgeModal';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

// Merchant-scoped library of Knowledge Bases. Each row shows source counts split
// by type (PDF · Markdown · Link), linked-agent count, and a verified pill. Open
// detail by clicking the row — navigates to /knowledge/:id.

export const KnowledgeListPage = observer(function KnowledgeListPage() {
  const { knowledgeStore: kb, merchantContextStore: ctx, themeStore } = useStore();
  const navigate = useNavigate();
  const isDark = themeStore.mode === 'dark';
  const [createOpen, setCreateOpen] = useState(false);

  // Re-fetch on every page mount. Without this, the page relies on the
  // store's reaction-on-merchantId-change, which doesn't re-fire when the
  // user navigates away and back. Same post-deploy stale-state failure
  // mode that AgentsListPage had — see that file's comment for the full
  // narrative.
  useEffect(() => {
    if (ctx.selectedMerchantId != null) {
      void kb.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<ReadOutlined />}
          title="Pick a merchant to begin"
          description="Use the merchant switcher in the top bar to scope the knowledge library."
          size="lg"
        />
      </SectionCard>
    );
  }

  function openDetail(row: Knowledge): void {
    trackEvent('Knowledge.Opened', { knowledgeId: row.id });
    navigate(`/knowledge/${row.id}`);
  }

  function moreMenu(row: Knowledge): MenuProps {
    return {
      items: [
        {
          key: 'open',
          icon: <EditOutlined />,
          label: 'Open & manage sources',
          onClick: () => openDetail(row),
        },
        {
          key: 'copy-id',
          icon: <CopyOutlined />,
          label: `Copy id #${row.id}`,
          onClick: () => {
            void navigator.clipboard?.writeText(String(row.id));
            trackEvent('Knowledge.IdCopied', { knowledgeId: row.id });
          },
        },
        { type: 'divider' as const },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete knowledge base',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: `Delete "${row.title ?? `Knowledge base #${row.id}`}"?`,
              width: 520,
              content: (
                <div>
                  <p style={{ marginTop: 8, marginBottom: 8 }}>
                    This will <b>permanently delete</b>:
                  </p>
                  <ul style={{ margin: '0 0 12px 18px', fontSize: 13, lineHeight: 1.7 }}>
                    <li>Every PDF, Markdown source, and Link inside this knowledge base</li>
                    <li>All uploaded files in storage</li>
                    <li>All search-index entries that ground agent responses</li>
                    <li>The connection between this knowledge base and every agent using it</li>
                  </ul>
                  <div
                    style={{
                      marginTop: 8,
                      padding: 10,
                      background: isDark ? colors.slate[700] : colors.amber[100],
                      color: isDark ? colors.amber[100] : colors.amber[700],
                      fontSize: 12.5,
                      borderRadius: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    <b>This cannot be undone.</b> Agents currently grounded in this knowledge base
                    will lose access to its sources and may answer differently afterward.
                  </div>
                </div>
              ),
              okText: 'Delete permanently',
              okButtonProps: { danger: true },
              cancelText: 'Cancel',
              onOk: async () => {
                try {
                  await kb.remove(row.id);
                  message.success(`Deleted "${row.title ?? `Knowledge base #${row.id}`}"`);
                  trackEvent('Knowledge.Deleted', { knowledgeId: row.id });
                } catch (err) {
                  message.error(err instanceof Error ? err.message : 'Failed to delete');
                }
              },
            });
          },
        },
      ],
    };
  }

  const columns: ColumnsType<Knowledge> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
      render: (text: string, row) => (
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: isDark ? colors.slate[50] : colors.slate[900],
              fontWeight: 600,
              fontSize: 13.5,
              letterSpacing: '-0.005em',
            }}
          >
            {text || <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(untitled)</span>}
            {row.verified && (
              <Tooltip title="Verified by an editor">
                <CheckCircleFilled style={{ color: colors.green[600], fontSize: 13 }} />
              </Tooltip>
            )}
          </div>
          {row.description && (
            <div
              style={{
                fontSize: 12,
                color: isDark ? colors.slate[400] : colors.slate[500],
                marginTop: 3,
                maxWidth: 520,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.description}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Sources',
      key: 'sources',
      width: 220,
      render: (_text, row) => {
        const pdf = row.pdfCount ?? 0;
        const md = row.markdownCount ?? 0;
        const lk = row.linkCount ?? 0;
        const total = pdf + md + lk;
        if (total === 0) {
          return <StatusPill tone="slate">Empty</StatusPill>;
        }
        return (
          <Space size={4} wrap>
            {pdf > 0 && (
              <SourceCount icon={<FilePdfOutlined />} count={pdf} label="PDF" tone="ink" />
            )}
            {md > 0 && (
              <SourceCount
                icon={<FileMarkdownOutlined />}
                count={md}
                label="MD"
                tone="violet"
              />
            )}
            {lk > 0 && (
              <SourceCount icon={<LinkOutlined />} count={lk} label="Link" tone="amber" />
            )}
          </Space>
        );
      },
    },
    {
      title: 'Agents',
      key: 'agents',
      width: 100,
      render: (_text, row) => {
        const count = row.agents?.length ?? 0;
        if (count === 0) return <StatusPill tone="slate">No agents</StatusPill>;
        return (
          <Tooltip
            title={
              row.agents && row.agents.length > 0 ? (
                <div style={{ maxWidth: 240 }}>
                  {row.agents.map((a) => (
                    <div key={a.id} style={{ fontSize: 12 }}>
                      • {a.displayName ?? a.a2AAgentName ?? `#${a.id}`}
                    </div>
                  ))}
                </div>
              ) : undefined
            }
          >
            <span>
              <StatusPill tone="ink">
                {count} {count === 1 ? 'agent' : 'agents'}
              </StatusPill>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (v: number) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
          }}
        >
          #{v}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          <Tooltip title="Open & manage sources">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<RightOutlined />}
              onClick={() => openDetail(row)}
            >
              Open
            </Button>
          </Tooltip>
          <Dropdown menu={moreMenu(row)} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="More actions" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="ink"
        icon={<ReadOutlined />}
        title={
          <Space size={6} align="center">
            <span>Knowledge</span>
            <HelpHotspot topic="knowledge.rag" />
          </Space>
        }
        subtitle={ctx.selectedMerchant?.title ?? '—'}
        count={kb.totalCount}
        countLabel={kb.totalCount === 1 ? 'knowledge base' : 'knowledge bases'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search knowledge bases…"
              allowClear
              style={{ width: 280 }}
              value={kb.search}
              onChange={(e) => kb.setSearch(e.target.value)}
              onSearch={() => void kb.fetch()}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void kb.fetch()}
              loading={kb.isLoading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setCreateOpen(true);
                trackEvent('Knowledge.CreateOpened');
              }}
            >
              New KB
            </Button>
          </Space>
        }
      />

      {kb.error && (
        <Alert
          type="error"
          message="Failed to load knowledge bases"
          description={kb.error}
          showIcon
          closable
        />
      )}

      <SectionCard noPadding>
        <Table<Knowledge>
          rowKey="id"
          columns={columns}
          dataSource={kb.items}
          loading={kb.isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            showTotal: (t) => `${t} knowledge ${t === 1 ? 'base' : 'bases'}`,
            style: { padding: '12px 18px', margin: 0 },
          }}
          onRow={(row) => ({
            onClick: () => openDetail(row),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<ReadOutlined />}
                title={kb.search ? 'No knowledge bases match' : 'No knowledge bases yet'}
                description={
                  kb.search
                    ? 'Try clearing the search to see all KBs.'
                    : 'Create a knowledge base to ground your agents in PDFs, Markdown, and live links.'
                }
                action={
                  !kb.search ? (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => setCreateOpen(true)}
                    >
                      Create knowledge base
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
        />
      </SectionCard>

      <CreateKnowledgeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          navigate(`/knowledge/${created.id}`);
        }}
      />
    </div>
  );
});

// ── Small helper: typed source-count pill ───────────────────────────────────

interface SourceCountProps {
  icon: React.ReactNode;
  count: number;
  label: string;
  tone: 'ink' | 'violet' | 'amber';
}

function SourceCount({ icon, count, label, tone }: SourceCountProps) {
  return (
    <StatusPill tone={tone} icon={icon}>
      {count} {label}
    </StatusPill>
  );
}
